import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { overlayClose } from '../../lib/overlayClose';

// Ordem canônica das formas de pagamento na roleta (deve bater com `methods` abaixo).
const METHOD_ORDER = ['dinheiro', 'pix', 'pix_maquininha', 'credito', 'debito', 'voucher', 'cheque', 'a_prazo'];

/**
 * Modal de forma de pagamento — inputs multi-forma + navegação por setas.
 * Toda a lógica de moveFocus/fillMethod fica aqui (bem contida ao modal).
 */
export const PDVPaymentModal = ({
  show, onClose,
  calculateTotal,
  paymentInputs, setPaymentInputs,
  onCredit, setOnCredit,
  setPaymentMethod,
  paymentMethod,
  creditDueDate, setCreditDueDate,
  customers, selectedCustomer, setSelectedCustomer,
  customerSearch, setCustomerSearch,
  customerHoverIdx, setCustomerHoverIdx,
  customerName, setCustomerName,
  customerCpf, setCustomerCpf,
  handleFinalizeSale, loading,
}) => {
  // hasCredit derivado dos inputs — precisa vir antes do useEffect (rules-of-hooks)
  const aPrazoAmount = parseFloat(paymentInputs?.a_prazo) || 0;
  const hasCredit = aPrazoAmount > 0.005;
  // Ref sincronizada com o método selecionado — evita closure desatualizada em setas rápidas.
  const paymentMethodRef = useRef(paymentMethod);
  paymentMethodRef.current = paymentMethod;
  // onCredit acompanha a seleção: verdadeiro quando há valor a prazo OU o método é "A Prazo"
  const wantsCredit = hasCredit || paymentMethod === 'a_prazo';
  useEffect(() => {
    if (setOnCredit && !!onCredit !== wantsCredit) setOnCredit(wantsCredit);
  }, [wantsCredit, onCredit, setOnCredit]);

  // Cliente obrigatório assim que "A Prazo" é a forma selecionada (mesmo sem valor digitado ainda).
  const requiresCustomerFocus = hasCredit || paymentMethod === 'a_prazo' || paymentMethod === 'credito_loja';

  // Fase do fluxo: 'select' (escolher a forma pelas setas ↑↓) ou 'value' (digitar o valor).
  const [payPhase, setPayPhase] = useState('select');
  const payPhaseRef = useRef('select'); payPhaseRef.current = payPhase;
  const paymentInputsRef = useRef(paymentInputs); paymentInputsRef.current = paymentInputs;
  const selectedCustomerRef = useRef(selectedCustomer); selectedCustomerRef.current = selectedCustomer;

  // ===== Helpers do fluxo em duas fases (100% teclado) =====
  const focusEl = (sel) => setTimeout(() => {
    const el = document.querySelector(sel);
    if (el) { el.focus(); if (el.select) el.select(); }
  }, 30);

  const computeDone = () => {
    const vals = paymentInputsRef.current || {};
    const t = calculateTotal();
    const paidAll = Object.values(vals).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const dv = parseFloat(vals.dinheiro) || 0;
    const remCash = Math.max(0, t - (paidAll - dv));
    const trocoCalc = dv > remCash ? dv - remCash : 0;
    return t > 0 && (paidAll - trocoCalc) >= t - 0.01;
  };

  // Saldo restante para uma forma = total − soma das OUTRAS formas já digitadas.
  const suggestRemainder = (key) => {
    const vals = paymentInputsRef.current || {};
    const t = calculateTotal();
    const others = Object.entries(vals).reduce((s, [k, v]) => (k === key ? s : s + (parseFloat(v) || 0)), 0);
    return Math.max(0, t - others);
  };

  // FASE SELEÇÃO: só destaca a forma (NÃO preenche nenhum valor).
  const highlightMethod = (key) => {
    setPaymentMethod(key);
    paymentMethodRef.current = key;
    if (setOnCredit) setOnCredit(key === 'a_prazo');
    setPayPhase('select'); payPhaseRef.current = 'select';
    focusEl('[data-testid="roulette-card"]');
  };

  const moveSelection = (dir) => {
    const cur = paymentMethodRef.current || 'dinheiro';
    const idx = METHOD_ORDER.indexOf(cur);
    const nextKey = METHOD_ORDER[(((idx < 0 ? 0 : idx) + dir) + METHOD_ORDER.length) % METHOD_ORDER.length];
    highlightMethod(nextKey);
  };

  // TRANSIÇÃO -> FASE VALOR: sugere o saldo restante (só se o campo estiver vazio) e foca o valor.
  const enterValuePhase = (key) => {
    setPaymentMethod(key); paymentMethodRef.current = key;
    setPayPhase('value'); payPhaseRef.current = 'value';
    const curVal = parseFloat((paymentInputsRef.current || {})[key]) || 0;
    if (curVal <= 0) {
      const rest = suggestRemainder(key);
      setPaymentInputs((prev) => ({ ...prev, [key]: rest > 0 ? rest.toFixed(2) : '' }));
    }
    focusEl(`[data-testid="pay-input-${key}"]`);
  };

  // PULO DO GATO: após ENTER no valor, grava e VOLTA para a SELEÇÃO (ou foca Confirmar se zerou o saldo).
  const commitValue = () => {
    setPayPhase('select'); payPhaseRef.current = 'select';
    setTimeout(() => {
      const el = document.querySelector(computeDone() ? '[data-testid="confirm-payment"]' : '[data-testid="roulette-card"]');
      if (el) el.focus();
    }, 40);
  };

  // Reset ao abrir: sempre inicia na fase de SELEÇÃO com o foco no card da roleta.
  useEffect(() => {
    if (!show) return;
    setPayPhase('select'); payPhaseRef.current = 'select';
    focusEl('[data-testid="roulette-card"]');
  }, [show]);

  // Teclado do modal (fonte única) — ativo somente com o modal aberto.
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      const tid = e.target?.dataset?.testid;
      // F3: pula o foco para o campo de Cliente (opcional) — e volta para a roleta se já estiver nele. 100% teclado.
      if (e.key === 'F3') {
        e.preventDefault(); e.stopPropagation();
        if (tid === 'pdv-customer-search') {
          setPayPhase('select'); payPhaseRef.current = 'select';
          focusEl('[data-testid="roulette-card"]');
        } else {
          focusEl('[data-testid="pdv-customer-search"]');
        }
        return;
      }
      if (tid === 'pdv-customer-search') return;   // campo de cliente gerencia as próprias teclas
      if (e.target?.tagName === 'BUTTON') return;   // botões usam Enter/Espaço nativo
      if (payPhaseRef.current === 'value') return;   // input de valor gerencia as próprias teclas

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault(); e.stopPropagation();
        moveSelection((e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1 : -1);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault(); e.stopPropagation();
        const cur = paymentMethodRef.current || 'dinheiro';
        if (computeDone()) { focusEl('[data-testid="confirm-payment"]'); return; }
        if (cur === 'a_prazo' && !selectedCustomerRef.current) { focusEl('[data-testid="pdv-customer-search"]'); return; }
        enterValuePhase(cur);
        return;
      }
      const k = e.key.toLowerCase();
      const map = { d: 'dinheiro', p: 'pix', c: 'credito', b: 'debito', v: 'voucher', a: 'a_prazo' };
      if (map[k] && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault(); e.stopPropagation();
        highlightMethod(map[k]);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
     
  }, [show]);

  if (!show) return null;

  const total = calculateTotal();
  const paid = Object.values(paymentInputs).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const dinheiroVal = parseFloat(paymentInputs.dinheiro) || 0;
  const otherPaid = paid - dinheiroVal;
  const remainingForCash = Math.max(0, total - otherPaid);
  const troco = dinheiroVal > remainingForCash ? dinheiroVal - remainingForCash : 0;
  const effectivePaid = paid - troco;
  const falta = total - effectivePaid;
  const canFinalize = effectivePaid >= total - 0.01;

  const methods = [
    { key: 'dinheiro', label: 'Dinheiro', short: '💵', hotkey: 'D' },
    { key: 'pix', label: 'PIX', short: '📱', hotkey: 'P' },
    { key: 'pix_maquininha', label: 'PIX Maq.', short: '📲' },
    { key: 'credito', label: 'Crédito', short: '💳', hotkey: 'C' },
    { key: 'debito', label: 'Débito', short: '💳', hotkey: 'B' },
    { key: 'voucher', label: 'Voucher', short: '🎟️', hotkey: 'V' },
    { key: 'cheque', label: 'Cheque', short: '🧾' },
    { key: 'a_prazo', label: 'A Prazo', short: '🕐', hotkey: 'A', isCredit: true },
  ];

  // Roleta: forma atualmente exibida + índice + resumo das formas já lançadas (venda mista)
  const current = methods.find((m) => m.key === paymentMethod) || methods[0];
  const currentIdx = methods.findIndex((m) => m.key === current.key);
  const chips = methods.filter((m) => (parseFloat(paymentInputs[m.key]) || 0) > 0);

  // Navegação pura entre formas (usada pelos chips): NÃO recalcula valores, só destaca.


  const requiresCustomer = requiresCustomerFocus;
  const search = (customerSearch || '').toLowerCase();
  const filtered = customers.filter(c =>
    !search ||
    c.name.toLowerCase().includes(search) ||
    (c.cpf_cnpj || '').includes(search) ||
    (c.phone || '').includes(search)
  ).slice(0, 20);

  const selectCustomer = (c) => {
    setSelectedCustomer(c.id);
    setCustomerName(c.name);
    setCustomerCpf(c.cpf_cnpj || '');
    setCustomerSearch(c.name);
    setCustomerHoverIdx(-1);
    selectedCustomerRef.current = c.id;
    // A Prazo: após escolher o cliente, segue para digitar o valor (ou Confirmar se já quitado).
    if (paymentMethodRef.current === 'a_prazo' || onCredit) {
      const hasVal = (parseFloat((paymentInputsRef.current || {}).a_prazo) || 0) > 0;
      if (hasVal || computeDone()) focusEl('[data-testid="confirm-payment"]');
      else enterValuePhase('a_prazo');
    } else {
      // Venda à vista (cliente opcional): após escolher, segue para Confirmar (se quitado) ou volta à roleta.
      setPayPhase('select'); payPhaseRef.current = 'select';
      focusEl(computeDone() ? '[data-testid="confirm-payment"]' : '[data-testid="roulette-card"]');
    }
  };
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerCpf('');
    setCustomerSearch('');
    selectedCustomerRef.current = null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg p-3 sm:p-4 w-full max-w-lg my-2 sm:my-4 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-900">Forma de Pagamento</h3>
          <div className="text-right">
            <div className="text-[9px] uppercase text-slate-500 font-bold leading-none">Total</div>
            <div className="text-xl font-black text-blue-600 leading-none mt-0.5">R$ {total.toFixed(2)}</div>
          </div>
        </div>

        {/* Display GIGANTE de Troco — atualiza em tempo real conforme o operador digita.
            Verde-lima pulsante quando pagamento é suficiente E há troco > 0.
            Amarelo quando pagamento suficiente sem troco.
            Cinza quando ainda falta valor. */}
        <div
          className={`mb-2 rounded-lg px-3 py-2 border-2 transition-all ${
              troco > 0 && canFinalize
                ? 'bg-lime-300 border-lime-500 animate-pulse'
                : canFinalize
                ? 'bg-emerald-100 border-emerald-400'
                : 'bg-slate-100 border-slate-300'
            }`}
            data-testid="troco-display"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className={`text-[10px] uppercase tracking-[0.25em] font-black leading-none ${
                  troco > 0 && canFinalize ? 'text-lime-900' : canFinalize ? 'text-emerald-800' : 'text-slate-500'
                }`}>
                  💰 Troco
                </p>
                <p className={`text-[10px] mt-1 leading-none ${canFinalize ? 'text-slate-700 font-bold' : 'text-slate-500'}`}>
                  {canFinalize
                    ? (troco > 0 ? 'Pode finalizar — devolver ao cliente' : 'Valor exato — pode finalizar')
                    : `Faltam R$ ${Math.max(0, total - effectivePaid).toFixed(2)}`}
                </p>
              </div>
              <div className={`text-4xl sm:text-5xl font-black tabular-nums leading-none ${
                troco > 0 && canFinalize
                  ? 'text-lime-900'
                  : canFinalize
                  ? 'text-emerald-700'
                  : 'text-slate-400'
              }`} data-testid="troco-display-value">
                R$ {troco.toFixed(2).replace('.', ',')}
              </div>
            </div>
          </div>

        <div className="space-y-2">
          {/* ===== ROLETA: Seletor Único de Forma de Pagamento =====
              Exibe UMA forma por vez. Setas ↑↓ (teclado ou botões) alternam a forma.
              O smartFill (paymentFill.js) preserva os valores já digitados e coloca só
              o RESTANTE na forma selecionada. O foco fica sempre no campo de valor. */}
          <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-xl p-3 shadow-inner" data-testid="payment-roulette-panel">
              <div className="flex items-stretch gap-2">
                {/* Setas verticais da roleta */}
                <div className="flex flex-col justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => moveSelection(-1)}
                    className="h-11 w-11 rounded-lg bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors active:scale-95"
                    data-testid="roulette-prev"
                    title="Forma anterior (↑)"
                  >
                    <ChevronUp className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSelection(1)}
                    className="h-11 w-11 rounded-lg bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors active:scale-95"
                    data-testid="roulette-next"
                    title="Próxima forma (↓)"
                  >
                    <ChevronDown className="h-6 w-6" />
                  </button>
                </div>

                {/* Card central da roleta (uma forma por vez) — fica destacado na fase SELEÇÃO */}
                <div
                  tabIndex={-1}
                  className={`flex-1 rounded-xl border-2 p-3 transition-all outline-none ${current.isCredit ? 'bg-yellow-50' : 'bg-white'} ${payPhase === 'select' ? 'border-blue-500 ring-4 ring-blue-500/30' : current.isCredit ? 'border-yellow-400' : 'border-slate-200'}`}
                  data-testid="roulette-card"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400" data-testid="roulette-position">
                      {currentIdx + 1} / {methods.length}
                    </span>
                    {current.hotkey && (
                      <span className="text-[9px] font-bold text-slate-400">
                        atalho <kbd className="px-1 bg-slate-200 rounded">{current.hotkey}</kbd>
                      </span>
                    )}
                  </div>
                  <div className="flex justify-center mt-1">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full transition-colors ${payPhase === 'value' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`} data-testid="roulette-phase">
                      {payPhase === 'value' ? '② Digite o valor e Enter' : '① Selecione a forma (↑ ↓) · Enter'}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-3xl leading-none">{current.short}</span>
                    <span className={`text-xl font-black ${current.isCredit ? 'text-yellow-800' : 'text-slate-900'}`} data-testid="roulette-label">
                      {current.label}
                    </span>
                  </div>
                  {/* Campo de valor — editável só na FASE VALOR; sugere o saldo restante ao entrar */}
                  <div className="mt-2 flex items-center justify-center gap-1">
                    <span className="text-2xl font-black text-slate-300">R$</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      readOnly={payPhase !== 'value'}
                      value={paymentInputs[current.key]}
                      onFocus={() => { if (payPhaseRef.current !== 'value') enterValuePhase(current.key); }}
                      onChange={(e) => {
                        // Aceita vírgula (BR) e ponto: converte vírgula -> ponto e mantém um único decimal.
                        const clean = e.target.value
                          .replace(/,/g, '.')
                          .replace(/[^\d.]/g, '')
                          .replace(/(\..*)\./g, '$1');
                        const next = { ...paymentInputs, [current.key]: clean };
                        // A Prazo já preenchido: recalcula para o saldo restante (total − à vista) enquanto digita
                        if (current.key !== 'a_prazo' && (parseFloat(next.a_prazo) || 0) > 0) {
                          const paid = Object.entries(next).reduce((s, [k, v]) => (k === 'a_prazo' ? s : s + (parseFloat(v) || 0)), 0);
                          const rest = Math.max(0, total - paid);
                          next.a_prazo = rest > 0.005 ? rest.toFixed(2) : '';
                        }
                        setPaymentInputs(next);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitValue(); }
                        else if (e.key === 'Escape') {
                          e.preventDefault();
                          setPayPhase('select'); payPhaseRef.current = 'select';
                          focusEl('[data-testid="roulette-card"]');
                        }
                      }}
                      placeholder="0,00"
                      className={`w-44 h-14 text-3xl font-black text-center px-2 transition-all ${payPhase === 'value' ? 'ring-2 ring-blue-500 border-blue-500' : 'bg-slate-50 text-slate-700 cursor-pointer'}`}
                      data-testid={`pay-input-${current.key}`}
                    />
                  </div>
                  {current.key === 'dinheiro' && troco > 0 && (
                    <div className="mt-1.5 bg-emerald-100 border border-emerald-300 rounded px-2 py-0.5 text-[10px] font-bold text-emerald-700 text-center" data-testid="pay-troco-badge">
                      Troco R$ {troco.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {/* Chips resumo das formas já lançadas (venda mista) */}
              {chips.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2" data-testid="payment-chips">
                  {chips.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => highlightMethod(m.key)}
                      className={`inline-flex items-center gap-1 rounded-full pl-2 pr-2.5 py-1 text-[10px] font-bold border transition-colors ${m.key === current.key ? 'bg-blue-600 text-white border-blue-600' : m.isCredit ? 'bg-yellow-400 text-yellow-950 border-yellow-500 hover:bg-yellow-300' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
                      data-testid={`payment-chip-${m.key}`}
                      title={`Ir para ${m.label}`}
                    >
                      <span>{m.short}</span>{m.label}
                      <span className="tabular-nums font-black">R$ {(parseFloat(paymentInputs[m.key]) || 0).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Resumo compacto Pago / Troco / Falta */}
              <div className="grid grid-cols-3 gap-1 mt-2 text-[9px]">
                <div className="bg-white border border-slate-200 rounded p-1 text-center">
                  <p className="uppercase font-bold text-slate-500 text-[8px] leading-tight">Pago</p>
                  <p className="font-black text-slate-900 text-xs leading-none">R$ {effectivePaid.toFixed(2)}</p>
                </div>
                <div className={`border rounded p-1 text-center ${troco > 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`} data-testid="pay-summary-troco">
                  <p className="uppercase font-bold text-slate-500 text-[8px] leading-tight">Troco</p>
                  <p className={`font-black text-xs leading-none ${troco > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>R$ {troco.toFixed(2)}</p>
                </div>
                <div className={`border rounded p-1 text-center ${falta > 0.01 ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`} data-testid="pay-summary-falta">
                  <p className="uppercase font-bold text-slate-500 text-[8px] leading-tight">{falta > 0.01 ? 'Falta' : 'Status'}</p>
                  <p className={`font-black text-xs leading-none ${falta > 0.01 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {falta > 0.01 ? `R$ ${falta.toFixed(2)}` : '✓ OK'}
                  </p>
                </div>
              </div>
              <p className="text-[9px] text-slate-300 text-center mt-2" data-testid="roulette-help">
                <kbd className="px-1 bg-white/20 rounded text-white">↑</kbd> <kbd className="px-1 bg-white/20 rounded text-white">↓</kbd> escolher a forma · <kbd className="px-1 bg-white/20 rounded text-white">Enter</kbd> digitar valor · <kbd className="px-1 bg-white/20 rounded text-white">Enter</kbd> grava e volta · saldo zerado → Enter em <b>Confirmar</b>
              </p>
            </div>

          {hasCredit && (
            <div className="bg-yellow-50 border border-yellow-300 rounded p-2 space-y-1" data-testid="credit-info-block">
              <p className="text-[11px] text-yellow-900 font-bold">
                🕐 Venda com A Prazo — R$ {aPrazoAmount.toFixed(2)} vira conta a receber
              </p>
              <p className="text-[10px] text-yellow-800">Cliente obrigatório · juros 2%/mês após 30 dias</p>
              <Label className="text-[10px]">Vencimento</Label>
              <Input
                type="date"
                value={creditDueDate}
                onChange={(e) => setCreditDueDate(e.target.value)}
                data-testid="credit-due-date"
                className="h-7 text-xs"
              />
            </div>
          )}

          <div className={`space-y-2 rounded p-2 border-2 ${requiresCustomer ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200 bg-white'}`}>
            <div className="space-y-1">
              <Label className={`text-xs ${requiresCustomer ? 'font-bold text-yellow-900' : ''}`}>
                Cliente {requiresCustomer ? '*' : '(opcional)'}
                {requiresCustomer
                  ? <span className="text-[10px] ml-2 text-yellow-800">obrigatório p/ a prazo</span>
                  : <span className="text-[10px] ml-2 text-slate-400">atalho <kbd className="px-1 bg-slate-200 rounded">F3</kbd> para vincular</span>}
              </Label>
              <div className="relative">
                <Input
                  value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setCustomerHoverIdx(0); }}
                  placeholder="Nome, CPF ou telefone..."
                  data-testid="pdv-customer-search"
                  className="h-8 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setCustomerHoverIdx((i) => Math.min(filtered.length - 1, i + 1));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setCustomerHoverIdx((i) => Math.max(0, i - 1));
                    } else if (e.key === 'Enter') {
                      const pickIdx = customerHoverIdx >= 0 ? customerHoverIdx : (filtered.length > 0 ? 0 : -1);
                      if (pickIdx >= 0 && filtered[pickIdx]) {
                        e.preventDefault();
                        selectCustomer(filtered[pickIdx]);
                      }
                    } else if (e.key === 'Escape') {
                      setCustomerSearch('');
                      setCustomerHoverIdx(-1);
                    }
                  }}
                />
                {selectedCustomer && (
                  <button type="button" onClick={clearCustomer} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-600 text-xs" data-testid="pdv-clear-customer">
                    ✕
                  </button>
                )}
              </div>
              {customerSearch && !selectedCustomer && filtered.length > 0 && (
                <div className="border border-slate-200 rounded max-h-32 overflow-y-auto bg-white" data-testid="customer-results">
                  {filtered.map((c, i) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseEnter={() => setCustomerHoverIdx(i)}
                      onClick={() => selectCustomer(c)}
                      className={`w-full text-left px-2 py-1 text-xs border-b border-slate-100 last:border-b-0 ${i === customerHoverIdx ? 'bg-blue-100' : 'hover:bg-slate-50'}`}
                      data-testid={`customer-result-${c.id}`}
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[10px] text-slate-500">{c.cpf_cnpj || ''} {c.phone ? `· ${c.phone}` : ''}</div>
                    </button>
                  ))}
                </div>
              )}
              {selectedCustomer && (
                <div className="bg-emerald-50 border border-emerald-300 rounded p-1.5 text-xs" data-testid="customer-selected-card">
                  <span className="font-bold text-emerald-900">✓ {customerName}</span>
                  {customerCpf && <span className="text-[10px] text-emerald-700 ml-2">CPF: {customerCpf}</span>}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancelar</Button>
            <Button onClick={handleFinalizeSale} disabled={loading} className="flex-1 h-9 text-sm bg-blue-600 hover:bg-blue-700" data-testid="confirm-payment">
              {loading ? 'Processando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
